import React, { Fragment, useState, useEffect } from "react";
import { Dialog, DialogPanel, Transition } from "@headlessui/react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faBuilding,
  faWrench,
  faDollarSign,
  faChartBar,
  faCog,
  faPlay,
  faUsers,
  faClipboardList,
  faCube,
  faTruck,
  faFileText,
  faTimes,
  faChevronDown,
  faChevronRight,
  faChartLine,
  faCogs,
  faTools,
  faShieldAlt,
  faExclamationTriangle,
  faLink,
  faSprayCan,
  faPercent,
  faCodeBranch,
  faCompass,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";

const Sidebar = ({ navigation, sidebarOpen, setSidebarOpen, currentPath }) => {
  const [expandedItems, setExpandedItems] = useState({});

  // Auto-expand parent menus when child routes are active
  useEffect(() => {
    const newExpandedItems = {};
    navigation.forEach((item) => {
      if (item.children && item.children.length > 0) {
        const hasActiveChild = item.children.some((child) =>
          isActive(child.href),
        );
        if (hasActiveChild) {
          newExpandedItems[item.name] = true;
        }
      }
    });
    setExpandedItems((prev) => ({ ...prev, ...newExpandedItems }));
  }, [currentPath, navigation]); // eslint-disable-line react-hooks/exhaustive-deps

  const getIcon = (iconName) => {
    const icons = {
      faHome,
      faBuilding,
      faWrench,
      faDollarSign,
      faChartBar,
      faCog,
      faPlay,
      faUsers,
      faClipboardList,
      faCube,
      faTruck,
      faFileText,
      faChartLine,
      faCogs,
      faTools,
      faShieldAlt,
      faExclamationTriangle,
      faLink,
      faSprayCan,
      faPercent,
      faCodeBranch,
      faCompass,
      faQuestionCircle,
    };
    return icons[iconName] || faHome;
  };

  const isActive = (href) => {
    if (href === "/dashboard") {
      return currentPath === "/dashboard";
    }
    return currentPath.startsWith(href);
  };

  const hasActiveChild = (children) => {
    return children.some((child) => isActive(child.href));
  };

  const toggleExpanded = (itemName) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemName]: !prev[itemName],
    }));
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center justify-center h-16 px-4 bg-[#0f172a]">
        <img
          src="/logo.png"
          alt="SSLG Budget logo"
          className="h-8 w-8 mr-2 rounded object-cover"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <h1 className="text-xl font-bold text-white">Bridge Budget</h1>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 bg-white space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const icon = getIcon(item.icon);
          const active = item.href ? isActive(item.href) : false;
          const hasChildren = item.children && item.children.length > 0;
          const isExpanded = expandedItems[item.name];
          const hasActiveChildItem = hasChildren
            ? hasActiveChild(item.children)
            : false;

          if (hasChildren) {
            return (
              <div key={item.name}>
                <button
                  onClick={() => toggleExpanded(item.name)}
                  className={`${
                    hasActiveChildItem
                      ? "bg-primary-100 border-primary-500 text-primary-700"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  } group flex items-center justify-between w-full px-2 py-2 text-sm font-medium rounded-md border-l-4 border-transparent`}
                >
                  <div className="flex items-center">
                    <FontAwesomeIcon
                      icon={icon}
                      className={`${
                        hasActiveChildItem
                          ? "text-primary-500"
                          : "text-gray-400 group-hover:text-gray-500"
                      } mr-3 flex-shrink-0 h-5 w-5`}
                    />
                    {item.name}
                  </div>
                  <FontAwesomeIcon
                    icon={isExpanded ? faChevronDown : faChevronRight}
                    className="h-4 w-4 text-gray-400"
                  />
                </button>

                {isExpanded && (
                  <div className="ml-6 mt-1 space-y-1">
                    {item.children.map((child) => {
                      const childIcon = getIcon(child.icon);
                      const childActive = isActive(child.href);

                      return (
                        <Link
                          key={child.name}
                          to={child.href}
                          className={`${
                            childActive
                              ? "bg-primary-50 border-primary-500 text-primary-700"
                              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                          } group flex items-center px-2 py-2 text-sm font-medium rounded-md border-l-4 border-transparent`}
                        >
                          <FontAwesomeIcon
                            icon={childIcon}
                            className={`${
                              childActive
                                ? "text-primary-500"
                                : "text-gray-400 group-hover:text-gray-500"
                            } mr-3 flex-shrink-0 h-4 w-4`}
                          />
                          {child.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          return (
            <Link
              key={item.name}
              to={item.href}
              className={`${
                active
                  ? "bg-primary-100 border-primary-500 text-primary-700"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              } group flex items-center px-2 py-2 text-sm font-medium rounded-md border-l-4 border-transparent`}
            >
              <FontAwesomeIcon
                icon={icon}
                className={`${
                  active
                    ? "text-primary-500"
                    : "text-gray-400 group-hover:text-gray-500"
                } mr-3 flex-shrink-0 h-5 w-5`}
              />
              {item.name}
            </Link>
          );
        })}
      </nav>
    </div>
  );

  return (
    <>
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 flex z-40 lg:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <DialogPanel className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <FontAwesomeIcon
                      icon={faTimes}
                      className="h-6 w-6 text-white"
                    />
                  </button>
                </div>
              </Transition.Child>

              <SidebarContent />
            </DialogPanel>
          </Transition.Child>

          <div className="flex-shrink-0 w-14" aria-hidden="true">
            {/* Force sidebar to shrink to fit close icon */}
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64">
          <SidebarContent />
        </div>
      </div>
    </>
  );
};

export default Sidebar;
